import { computed, onScopeDispose, ref, shallowRef, watch } from 'vue';
import type { ComputedRef, Ref, ShallowRef } from 'vue';
import {
  DISH_FIELDS,
  NO_DISH_FORM_ERRORS,
  dishFieldChanged,
  dishForm,
  dropEcho,
  readDishForm,
  settleEcho,
} from '@/domain/menu-import/item-editor';
import type {
  IDishForm,
  IDishFormErrors,
  IPriceSizesForm,
  TDishField,
} from '@/domain/menu-import/item-editor';
import type { IDraftItem, TDraftOp } from '@/interfaces/IMenuImport';

export interface IDishFieldsOptions {
  /** The dish as the server has it (replaced on every delta). */
  item: () => IDraftItem;
  /** The store's country (price formats). */
  country: () => string;
}

export type TDishFieldsCommit =
  | { status: 'valid'; ops: TDraftOp[] }
  | { status: 'invalid' };

export interface IUseDishFields {
  form: Readonly<Ref<IDishForm>>;
  errors: Readonly<Ref<IDishFormErrors>>;
  /** Some field holds a typed change not saved yet (or an invalid value). */
  hasChanges: ComputedRef<boolean>;
  setName(text: string): void;
  setDescription(text: string): void;
  setPriceSizes(value: IPriceSizesForm): void;
  focusField(field: TDishField): void;
  blurField(field: TDishField): void;
  /**
   * Reads `fields`: untouched ones take the server copy again, typed ones
   * give the operations that save them (the form keeps the stored values).
   * Invalid: nothing is saved and `errors` says where.
   */
  commit(fields?: readonly TDishField[]): TDishFieldsCommit;
}

function noDirt(): Record<TDishField, boolean> {
  return { name: false, description: false, priceSizes: false };
}

/**
 * The name, description and price or sizes of a dish while the owner edits
 * them (plan §4.5.1, §4.5.4). A field the owner is typing in (focused or
 * with unsaved text) keeps its value when a delta replaces the dish; the
 * others follow the server copy, and a field is read from the server again
 * when it is left untouched.
 */
export function useDishFields(options: IDishFieldsOptions): IUseDishFields {
  const form = ref<IDishForm>(dishForm(options.item(), options.country()));
  const errors = ref<IDishFormErrors>({ ...NO_DISH_FORM_ERRORS });
  const dirty = ref<Record<TDishField, boolean>>(noDirt());
  let focused: TDishField | null = null;

  function setDirty(field: TDishField, value: boolean) {
    dirty.value = { ...dirty.value, [field]: value };
  }

  function clearError(field: TDishField) {
    if (field === 'name') errors.value = { ...errors.value, name: false };
    if (field === 'priceSizes')
      errors.value = { ...errors.value, priceSizes: null };
  }

  function takeField(field: TDishField, source: IDishForm) {
    if (field === 'name') form.value = { ...form.value, name: source.name };
    else if (field === 'description')
      form.value = { ...form.value, description: source.description };
    else form.value = { ...form.value, priceSizes: source.priceSizes };
  }

  /** The server copy of one field, the field clean again. */
  function reread(field: TDishField, item: IDraftItem) {
    takeField(field, dishForm(item, options.country()));
    setDirty(field, false);
    clearError(field);
  }

  // A delta replaced the dish: every field the owner is not typing in follows it.
  watch(options.item, (next, previous) => {
    for (const field of DISH_FIELDS) {
      if (field === focused || dirty.value[field]) continue;
      if (!previous || dishFieldChanged(field, previous, next))
        reread(field, next);
    }
  });

  function setName(text: string) {
    form.value = { ...form.value, name: text };
    setDirty('name', true);
    clearError('name');
  }

  function setDescription(text: string) {
    form.value = { ...form.value, description: text };
    setDirty('description', true);
  }

  function setPriceSizes(value: IPriceSizesForm) {
    form.value = { ...form.value, priceSizes: value };
    setDirty('priceSizes', true);
    clearError('priceSizes');
  }

  function focusField(field: TDishField) {
    focused = field;
  }

  function blurField(field: TDishField) {
    if (focused === field) focused = null;
  }

  function commit(
    fields: readonly TDishField[] = DISH_FIELDS
  ): TDishFieldsCommit {
    const item = options.item();
    const typed = fields.filter((field) => dirty.value[field]);
    fields
      .filter((field) => !dirty.value[field])
      .forEach((field) => reread(field, item));
    if (typed.length === 0) return { status: 'valid', ops: [] };

    const read = readDishForm(item, form.value, typed, options.country());
    if (read.status === 'invalid') {
      const next = { ...errors.value };
      if (typed.includes('name')) next.name = read.errors.name;
      if (typed.includes('priceSizes'))
        next.priceSizes = read.errors.priceSizes;
      errors.value = next;
      return { status: 'invalid' };
    }
    for (const field of typed) {
      takeField(field, read.form);
      setDirty(field, false);
      clearError(field);
    }
    return { status: 'valid', ops: read.ops };
  }

  const hasChanges = computed(() => {
    const typed = DISH_FIELDS.filter((field) => dirty.value[field]);
    if (typed.length === 0) return false;
    const read = readDishForm(
      options.item(),
      form.value,
      typed,
      options.country()
    );
    return read.status === 'invalid' || read.ops.length > 0;
  });

  return {
    form,
    errors,
    hasChanges,
    setName,
    setDescription,
    setPriceSizes,
    focusField,
    blurField,
    commit,
  };
}

export interface IUseOptimisticValue<T> {
  /** The owner's latest value until the server copy catches up. */
  value: Readonly<ShallowRef<T>>;
  /**
   * Shows `next` at once. `saved` is its save: when it resolves null (the
   * save was refused), the value goes back to the server copy.
   */
  set(next: T, saved?: Promise<unknown>): void;
}

/**
 * A dish value the owner changes with taps (category, allergens, hidden,
 * photo): shown at once, while the saves go through the queue in the
 * background, so quick taps never see an older server copy in between. A
 * refused save never leaves the owner's value on screen.
 */
export function useOptimisticValue<T>(server: () => T): IUseOptimisticValue<T> {
  const value = shallowRef<T>(server());
  let pending: T[] = [];
  let active = true;

  watch(server, (next) => {
    const settled = settleEcho(pending, next);
    pending = settled.pending;
    if (settled.adopt) value.value = next;
  });

  function refused(sent: T) {
    if (!active) return;
    const settled = dropEcho(pending, sent);
    pending = settled.pending;
    if (settled.adopt) value.value = server();
  }

  function set(next: T, saved?: Promise<unknown>) {
    value.value = next;
    pending = [...pending, next];
    void saved?.then((result) => {
      if (result === null) refused(next);
    });
  }

  onScopeDispose(() => {
    active = false;
  });

  return { value, set };
}
